<template>
    <div class="row mt-4">

        <!-- SIDEBAR -->
        <div class="col-12 col-md-3 col-lg-2">
            <div class="d-flex justify-content-start align-items-center mb-3">
                <b-button 
                    variant="sharp-primary" 
                    size="lg"
                    block
                    :disabled="!hasPermissionToCreate || loadingNotes"
                    @click="handleCreateNewNoteClick" 
                >
                    <b-icon icon="plus-circle" class="mr-2"></b-icon>
                    {{ $t('vendorNotesPage.newNote') }}
                </b-button>
            </div>
            <div class="mb-3">
                <label class="text-uppercase font-size-70 mb-1">{{ $t('vendorNotesPage.filterLabel') }}</label>
                <b-form-select 
                    v-model="onlyMyNotes"
                    :options="myNotesOptions"
                    class="w-100"
                ></b-form-select>
            </div>
            <div class="">  <!-- move these to a sidebar with new button and links to jump to certain past months/years -->
                <div class="small text-muted mt-3">{{ $t('vendorNotesPage.visibilityNote') }}</div>
                <!-- <div class="small text-muted mt-3">Notes are not visible to suppliers.</div> -->
                <div class="small text-muted mt-3">{{ $t('vendorNotesPage.sortNote') }}</div>
            </div>
        </div>
        
        <!-- NOTE LIST -->
        <div class="col-12 col-md-9 col-lg-10">
            <div v-if="initialLoadComplete && !hasFilteredNotes" key="noNoteList" class="">
                <div class="bg-gray-100 min-height-200 border rounded-lg d-flex align-items-center justify-content-center text-uppercase font-weight-500 font-size-80 text-muted">
                    {{ $t('vendorNotesPage.noNotes') }}
                </div>
            </div>
            <div v-else key="noteList">
                <transition-group name="vendor-note-list-wrapper" tag="div" class="d-flex flex-column gap-4">
                    <div v-for="(note) in noteList" :key="note.id + '_note_line'">
                        <VendorNoteLine
                            :companyId="companyId"
                            :companyName="companyName"
                            :note="note.data"
                            :editMode="note.editMode"
                            @note-saved="handleNoteSaved"
                            @note-edit-cancel="handleNoteEditCancel"
                            @delete-note="handleNoteDelete"
                        ></VendorNoteLine>
                    </div>
                </transition-group>
            </div>
        </div>

    </div>
</template>


<script>
import VendorNoteLine from '../components/VendorNoteLine.vue';
import { suppliosApi } from '@/js/api-service';
import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';
import { DateTime } from 'luxon';
import orderBy from 'lodash/orderBy';

class LocalNote {  // we add things here so we can track edit mode on front-end
    constructor({data, editMode = false}) {
        this.id = data.id;
        this.data = data;
        this.editMode = editMode;
        this.timestamp = DateTime.fromISO(data.created_at).toMillis();  // used for sorting
    }
}

export default {
    props: ["resource"],
    components: {
        VendorNoteLine,
    },
    mixins: [ 
        errorHandling,
        currentUserInfoMixin,
    ],
    i18n: {
        messages: {
            en: {
                vendorNotesPage: {
                    newNote: 'New Note',
                    filterLabel: 'Filter',
                    visibilityNote: 'Notes are visible to all internal users, but not visible to suppliers.',
                    sortNote: 'Most recent notes are listed first.',
                    noNotes: 'No Notes',
                    allNotes: 'All Notes',
                    myNotes: 'My Notes',
                    defaultNoteTitle: 'New note - {date}',
                    errorGetPermissions: "Error while getting user's permissions",
                    errorFetchNotes: 'Error while fetching the notes',
                    errorDeleteNote: 'Error while deleting the notes',
                    errorCreateNote: 'Error while creating new note',
                },
            },
            es: {
                vendorNotesPage: {
                    newNote: 'Nueva Nota',
                    filterLabel: 'Filtro',
                    visibilityNote: 'Las notas son visibles para todos los usuarios internos, pero no son visibles para los proveedores.',
                    sortNote: 'Las notas más recientes aparecen primero.',
                    noNotes: 'Sin Notas',
                    allNotes: 'Todas las Notas',
                    myNotes: 'Mis Notas',
                    defaultNoteTitle: 'Nueva nota - {date}',
                    errorGetPermissions: 'Error al obtener los permisos del usuario',
                    errorFetchNotes: 'Error al cargar las notas',
                    errorDeleteNote: 'Error al eliminar la nota',
                    errorCreateNote: 'Error al crear una nueva nota',
                },
            },
            pt: {
                vendorNotesPage: {
                    newNote: 'Nova Nota',
                    filterLabel: 'Filtro',
                    visibilityNote: 'As notas são visíveis para todos os usuários internos, mas não são visíveis para os fornecedores.',
                    sortNote: 'As notas mais recentes aparecem primeiro.',
                    noNotes: 'Sem Notas',
                    allNotes: 'Todas as Notas',
                    myNotes: 'Minhas Notas',
                    defaultNoteTitle: 'Nova nota - {date}',
                    errorGetPermissions: 'Erro ao obter as permissões do usuário',
                    errorFetchNotes: 'Erro ao carregar as notas',
                    errorDeleteNote: 'Erro ao excluir a nota',
                    errorCreateNote: 'Erro ao criar uma nova nota',
                },
            },
            'zh-Hans': {
                vendorNotesPage: {
                    newNote: '新建备注',
                    filterLabel: '筛选',
                    visibilityNote: '备注对所有内部用户可见，但对供应商不可见。',
                    sortNote: '最新的备注显示在最前面。',
                    noNotes: '暂无备注',
                    allNotes: '全部备注',
                    myNotes: '我的备注',
                    defaultNoteTitle: '新备注 - {date}',
                    errorGetPermissions: '获取用户权限时出错',
                    errorFetchNotes: '加载备注时出错',
                    errorDeleteNote: '删除备注时出错',
                    errorCreateNote: '创建新备注时出错',
                },
            },
            'zh-Hant': {
                vendorNotesPage: {
                    newNote: '新增備註',
                    filterLabel: '篩選',
                    visibilityNote: '備註對所有內部使用者可見，但對供應商不可見。',
                    sortNote: '最新的備註顯示在最前面。',
                    noNotes: '沒有備註',
                    allNotes: '全部備註',
                    myNotes: '我的備註',
                    defaultNoteTitle: '新備註 - {date}',
                    errorGetPermissions: '取得使用者權限時發生錯誤',
                    errorFetchNotes: '載入備註時發生錯誤',
                    errorDeleteNote: '刪除備註時發生錯誤',
                    errorCreateNote: '建立新備註時發生錯誤',
                },
            },
        },
    },
    data: function() {
        return {
            initialLoadComplete: false,
            loadingNotes: false,
            companyId: this.resource.id,
            companyName: this.resource.name,
            hasPermissionToCreate: false,
            noteStore: {},  // use this.$set() to update - these are localNote objects
            onlyMyNotes: false,
        }
    },
    computed: {
        myNotesOptions() {
            return [
                {value: false, text: this.$t('vendorNotesPage.allNotes')},
                {value: true, text: this.$t('vendorNotesPage.myNotes')},
            ];
        },
        noteList() {
            const allnotes = orderBy(Object.values(this.noteStore), ['timestamp'], ['desc']);
            return this.onlyMyNotes ? allnotes.filter((note) => note.data.created_by_id === this.$currentUserInfo.tenant_user_id) : allnotes;
        },
        hasAnyNotes() {
            return Object.values(this.noteStore).length > 0;
        },
        hasFilteredNotes() {
            return this.noteList.length > 0;
        },
    },
    mounted() {
        this.getPermissions();
        this.fetchNotes();
    },
    methods: {
        async getPermissions() {
            try {
                const {data} = await suppliosApi.vendorStandard.get_note_permissions(this.companyId);
                this.hasPermissionToCreate = data['create_note_permission']
            }
            catch(error) {
                this.apiErrorHandler(error, this.$t('vendorNotesPage.errorGetPermissions'));
            }
        },
        setLocalNote(localNote) {
            // pass in instance of LocalNote
            this.$set(this.noteStore, localNote.id, localNote);
        },
        async fetchNotes() {
            this.loadingNotes = true;
            let notes = [];
            try {
                const {data} = await suppliosApi.vendorStandard.get_notes(this.companyId);
                notes = data['vendor_notes'];
            }
            catch(error) {
                this.apiErrorHandler(error, this.$t('vendorNotesPage.errorFetchNotes'));
                notes = [];
            }
            this.noteStore = {};
            notes.forEach((noteData) => {
                const localNote = new LocalNote({
                    data: noteData,
                    editMode: false,
                });
                this.setLocalNote(localNote);
            });
            this.initialLoadComplete = true;
            this.loadingNotes = false;
        },
        async handleNoteDelete(data) {
            try {
                await suppliosApi.vendorStandard.delete_note(data.noteId, this.companyId);
                await this.fetchNotes();
            }
            catch(error) {
                this.apiErrorHandler(error, this.$t('vendorNotesPage.errorDeleteNote'));
            }
        },
        getDefaultNoteTitle() {
            const currentDateString = DateTime.local().setLocale(this.$splLocale).toLocaleString(DateTime.DATE_FULL);
            return this.$t('vendorNotesPage.defaultNoteTitle', { date: currentDateString });
            //return `New note for ${this.companyName}`;
        },
        async handleCreateNewNoteClick() {
            const newNoteData = {
                title: this.getDefaultNoteTitle(),
                body: '',
            };
            let resp;
            try {
                resp = await suppliosApi.vendorStandard.create_note(this.companyId, newNoteData);
                //await this.fetchNotes();
            }
            catch(error) {
                this.apiErrorHandler(error, this.$t('vendorNotesPage.errorCreateNote'));
            }
            if (resp?.data?.vendor_note) {
                const newNote = new LocalNote({
                    data: resp.data.vendor_note,
                    editMode: true,
                });
                this.setLocalNote(newNote);
            }
        },
        async handleNoteSaved(updatedNoteData) {
            // no refetching of all notes, child component passes up cloned copy of updated note.data, just set that in store
            // await this.fetchNotes();
            const updatedLocalNote = new LocalNote({
                data: updatedNoteData,
                editMode: false,
            });
            this.setLocalNote(updatedLocalNote);
        },
        async handleNoteEditCancel() {
            // no need to do anything anymore.... child component re-clones the data we pass to it, only tells us after it saves changes
            //await this.fetchNotes();
        }
    }
};
</script>

<style lang="scss">

/* this is transition group */
.vendor-note-list-wrapper-item {
    // put things here that go on each item
}
.vendor-note-list-wrapper-enter-active, .vendor-note-list-wrappe-leave-active {
    transition: all 0.5s ease;
}
.vendor-note-list-wrapper-enter, .vendor-note-list-wrapper-leave-to /* .list-leave-active below version 2.1.8 */ {
  opacity: 0;
  //transform: translateY(30px);
  transform: scale(0.9);
}
.vendor-note-list-wrapper-move {
    transition: transform 0.5s ease;
}

</style>